/**
 * 动态内容块扩展工具
 * 实现"上下文驱动的模板扩展"机制
 *
 * 核心功能：
 * 1. 检测 businessContext.contentBlocks 中的 dataSources 字段
 * 2. 对包含 dataSources 的内容块进行模板占位符复制
 * 3. 为每个数据源生成独立的内容块定义
 */

/**
 * 根据内容块类型从数据源中提取正确的数据字段
 * @param {string} contentBlockType - 内容块类型
 * @param {Object} dataSource - 数据源对象
 * @returns {any} 提取的数据
 */
const extractDataForContentBlockType = (contentBlockType, dataSource) => {
    switch (contentBlockType) {
        case "scheduleFlightList":
        case "scheduleFlightListWithComparison":
            // 航班列表类型：提取 flights 字段
            return dataSource.flights || dataSource;
        case "scheduleInfo":
        case "multiScheduleInfo":
            // 计划信息类型：提取 scheduleInfo 字段，如果没有则使用整个数据源
            return dataSource.scheduleInfo || dataSource;
        default:
            // 其他类型：直接使用数据源
            console.warn(`[DynamicExpansion] 未知的内容块类型: ${contentBlockType}，使用原始数据源`);
            return dataSource;
    }
};

/**
 * 检查内容块是否需要动态扩展
 * @param {Object} contentBlock - 内容块定义
 * @returns {boolean} 是否需要扩展
 */
export const needsExpansion = (contentBlock) => {
    const result = contentBlock && contentBlock.dataSources && Array.isArray(contentBlock.dataSources) && contentBlock.dataSources.length > 1;

    if (contentBlock?.dataSources && !Array.isArray(contentBlock.dataSources)) {
        console.warn("[DynamicExpansion] dataSources 应该是数组格式", {
            blockId: contentBlock.id,
            dataSourcesType: typeof contentBlock.dataSources,
        });
    }

    return result;
};

/**
 * 扩展模板内容，为包含 dataSources 的内容块复制占位符
 * @param {string} templateContent - 原始模板内容
 * @param {Object} businessContext - 业务上下文
 * @returns {string} 扩展后的模板内容
 */
export const expandTemplateContent = (templateContent, businessContext) => {
    if (!templateContent || !businessContext?.contentBlocks) {
        return templateContent;
    }

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = templateContent;

    // 查找需要扩展的内容块
    businessContext.contentBlocks.forEach((contentBlock) => {
        if (needsExpansion(contentBlock)) {
            // 查找所有对应的占位符元素（可能有多个，如行内和块级）
            const placeholderElements = tempDiv.querySelectorAll(`[data-content-block-id="${contentBlock.id}"]`);

            if (placeholderElements.length > 0) {
                // 为每个占位符元素进行扩展
                placeholderElements.forEach((placeholderElement, placeholderIndex) => {
                    const isInline = placeholderElement.getAttribute("data-is-inline") === "true";

                    // 为每个数据源创建新的占位符
                    const newPlaceholders = contentBlock.dataSources.map((dataSource, sourceIndex) => {
                        const newElement = placeholderElement.cloneNode(true);
                        const newId = sourceIndex === 0 ? contentBlock.id : `${contentBlock.id}_${sourceIndex}`;

                        // 更新新占位符的ID
                        newElement.setAttribute("data-content-block-id", newId);

                        // 可选：添加数据源索引标识
                        newElement.setAttribute("data-source-index", sourceIndex.toString());

                        return newElement;
                    });

                    // 替换原始占位符
                    const parentElement = placeholderElement.parentNode;
                    const nextSibling = placeholderElement.nextSibling;

                    // 移除原始占位符
                    placeholderElement.remove();

                    // 插入新的占位符
                    newPlaceholders.forEach((newPlaceholder, index) => {
                        if (nextSibling) {
                            parentElement.insertBefore(newPlaceholder, nextSibling);
                        } else {
                            parentElement.appendChild(newPlaceholder);
                        }

                        // 对于行内元素，在多个占位符之间添加分隔符
                        if (isInline && index < newPlaceholders.length - 1) {
                            const separator = document.createTextNode(" & ");
                            if (nextSibling) {
                                parentElement.insertBefore(separator, nextSibling);
                            } else {
                                parentElement.appendChild(separator);
                            }
                        }
                    });
                });
            }
        }
    });

    const expandedTemplate = tempDiv.innerHTML;
    return expandedTemplate;
};

/**
 * 扩展业务上下文，为包含 dataSources 的内容块生成独立的内容块定义
 * @param {Object} businessContext - 原始业务上下文
 * @returns {Object} 扩展后的业务上下文
 */
export const expandBusinessContext = (businessContext) => {
    if (!businessContext?.contentBlocks) {
        return businessContext;
    }

    const expandedContentBlocks = [];

    businessContext.contentBlocks.forEach((contentBlock) => {
        if (needsExpansion(contentBlock)) {
            // 为每个数据源创建独立的内容块定义
            contentBlock.dataSources.forEach((dataSource, index) => {
                const newContentBlock = {
                    ...contentBlock,
                    // 更新ID，第一个保持原ID，后续添加索引
                    id: index === 0 ? contentBlock.id : `${contentBlock.id}_${index}`,
                    // 使用对应的数据源数据
                    // 根据内容块类型提取正确的数据字段
                    data: extractDataForContentBlockType(contentBlock.type, dataSource),
                    // 更新标题以区分不同的数据源
                    title: index === 0 ? contentBlock.title : `${contentBlock.title} (${index + 1})`,
                    // 移除 dataSources 字段，因为已经展开
                    dataSources: undefined,
                    // 保存完整的数据源信息，供需要时使用
                    _fullDataSource: dataSource,
                    // 添加源信息用于调试
                    _expansionInfo: {
                        originalId: contentBlock.id,
                        sourceIndex: index,
                        isExpanded: true,
                    },
                };

                expandedContentBlocks.push(newContentBlock);
            });
        } else {
            // 不需要扩展的内容块直接保留
            expandedContentBlocks.push(contentBlock);
        }
    });

    const expandedContext = {
        ...businessContext,
        contentBlocks: expandedContentBlocks,
    };

    return expandedContext;
};

/**
 * 主要的扩展函数，同时处理模板和业务上下文的扩展
 * @param {string} templateContent - 原始模板内容
 * @param {Object} businessContext - 原始业务上下文
 * @returns {{expandedTemplateContent: string, expandedBusinessContext: Object}} 扩展结果
 */
export const expandDynamicContentBlocks = (templateContent, businessContext) => {
    // 首先基于原始业务上下文扩展模板（因为模板中的占位符ID是原始的）
    const expandedTemplateContent = expandTemplateContent(templateContent, businessContext);

    // 然后扩展业务上下文
    const expandedBusinessContext = expandBusinessContext(businessContext);

    return {
        expandedTemplateContent,
        expandedBusinessContext,
    };
};

/**
 * 检查是否需要进行动态扩展
 * @param {Object} businessContext - 业务上下文
 * @returns {boolean} 是否需要扩展
 */
export const hasExpandableContentBlocks = (businessContext) => {
    if (!businessContext?.contentBlocks) {
        return false;
    }

    return businessContext.contentBlocks.some(needsExpansion);
};
